// LE-00 (promo / M-49): 顧客管理「分析サマリー」の集計（pure / 非破壊）。
// useAllUsers が返す AppUser[]（denormalize 済み）だけから client-side で集計する。
// 追加 I/O ゼロ。可視化（Tailwind バー）は CustomerAnalytics コンポーネントが担う。

import type { AppUser, PromoEventType, PromoInterestKey } from '@kawai/shared';
import { isAwaitingReply } from '@kawai/shared';
import { INTEREST_KEY_OPTIONS, SEGMENT_EVENT_TYPE_OPTIONS } from './segment.js';
import type { UserCouponActivity } from './couponStats.js';

/** 予約経験の有無（M-47 / dataSource 欠落の既存 doc も bookingCount で正しく分類）。 */
export function isBookedCustomer(u: Pick<AppUser, 'bookingCount'>): boolean {
  return (u.bookingCount ?? 0) > 0;
}

/**
 * 予約フォームで入力された実名を `顧客名（カナ）` 形式に整形する（M-50 / pure）。
 * LINE 表示名の隣に併記するために使う。実名が無ければ `null`（カナのみは括弧を出さない）。
 */
export function formatBookingName(
  u: Pick<AppUser, 'bookingName' | 'bookingKana'>,
): string | null {
  const name = u.bookingName?.trim();
  if (!name) return null;
  const kana = u.bookingKana?.trim();
  return kana ? `${name}（${kana}）` : name;
}

/**
 * 行動アクションタグのキー（M-59 / 旧「反応のみ」区分の細分化）。
 * `eventTypeCounts`（行動ログの種別ごと累計）から導出する、予約有無に依らない
 * **全ユーザー対象**の分類軸。
 * - `'coupon_redeem'` / `'coupon_claim'` / `'survey_answer'` / `'richmenu_tap'` /
 *   `'liff_link_tap'`: 該当行動を 1 回以上経験（非排他 / coupon_redeem は M-67、
 *   survey_answer は M-73 仕上げで追加）
 * - `'liff_visit'`: 上記の能動アクションが無く LIFF 来訪のみ（排他）
 * - `'none'`: 行動ログが皆無（webhook follow 等 / 予約客で行動ログ未記録も含む）
 */
export type ReactionActionKey =
  | 'coupon_redeem'
  | 'coupon_claim'
  | 'survey_answer'
  | 'richmenu_tap'
  | 'liff_link_tap'
  | 'liff_visit'
  | 'none';

/** 行動アクションタグの表示・集計順（強い反応 → 弱い反応 → その他）。 */
export const REACTION_ACTION_ORDER: readonly ReactionActionKey[] = [
  'coupon_redeem',
  'coupon_claim',
  // M-73 仕上げ: アンケート回答は自己申告の能動反応（M-59 申し送り② / 回答リンクは /track を
  // 通らず liff_link_tap が付かないため、無いと回答のみのユーザーが「その他」に誤分類される）。
  'survey_answer',
  'richmenu_tap',
  'liff_link_tap',
  'liff_visit',
  'none',
] as const;

// フィルタキーの振り分けに使う（`matchesCustomerFilter`）。
// 前提: 行動アクションキー（coupon_claim 等）とリッチメニュー item キー
// （`RICHMENU_LAYOUT` の item: reservation / inspection / route 等）は名前空間が重複しない。
// 衝突すると item フィルタが行動アクションとして誤判定されるため、item 追加時は
// REACTION_ACTION_ORDER の全キー（現在 7 キー）と同名にしないこと（現状は衝突なし）。
const REACTION_ACTION_KEY_SET: ReadonlySet<string> = new Set(REACTION_ACTION_ORDER);

/**
 * ユーザーの行動アクションタグを導出する（pure / 全ユーザー対象 / M-59）。
 *
 * `coupon_redeem` / `coupon_claim` / `survey_answer` / `richmenu_tap` / `liff_link_tap` は
 * 非排他（複数該当しうる）。能動アクションが 1 つも無い場合のみ `liff_visit`（来訪のみ）、
 * それも無ければ `none`。戻り値は必ず 1 つ以上を含む（最低でも `none`）。
 *
 * M-68: `coupon` を渡すと coupon_usages 由来のアクティビティと**和集合**で判定する
 * （coupon_redeem イベント導入前の過去の取得・使用にもタグを出すため）。
 * クーポン系も能動アクション扱い = 該当すれば liff_visit / none は付かない。
 */
export function reactionActionsOf(
  u: Pick<AppUser, 'eventTypeCounts'>,
  coupon?: UserCouponActivity,
): ReactionActionKey[] {
  const c = u.eventTypeCounts ?? {};
  const actions: ReactionActionKey[] = [];
  if ((c.coupon_redeem ?? 0) > 0 || coupon?.redeemed) actions.push('coupon_redeem');
  if ((c.coupon_claim ?? 0) > 0 || coupon?.claimed) actions.push('coupon_claim');
  if ((c.survey_answer ?? 0) > 0) actions.push('survey_answer');
  if ((c.richmenu_tap ?? 0) > 0) actions.push('richmenu_tap');
  if ((c.liff_link_tap ?? 0) > 0) actions.push('liff_link_tap');
  if (actions.length === 0 && (c.liff_visit ?? 0) > 0) actions.push('liff_visit');
  if (actions.length === 0) actions.push('none');
  return actions;
}

/**
 * 顧客一覧フィルターの選択キー。
 * - `'booked'`: 予約客（bookingCount > 0）
 * - `ReactionActionKey`: 行動アクションタグ（全ユーザー対象 / 例 `'coupon_claim'`）
 * - それ以外: リッチメニュー item キー（`RICHMENU_LAYOUT` の item / 例 `'inspection'`）
 */
export type CustomerFilterKey = 'booked' | 'awaiting_reply' | ReactionActionKey | string;

/**
 * 複数選択フィルター（OR）にユーザーが一致するか判定する（pure）。
 *
 * - `selected` が空集合なら全件一致（= 「すべて」）。
 * - 選択肢のいずれか 1 つでも該当すれば一致（OR）:
 *   - `'booked'`: 予約客（bookingCount > 0）
 *   - 行動アクションキー: そのユーザーの `reactionActionsOf` に含む（予約有無に依らない）
 *   - item キー: そのユーザーが当該リッチメニューボタンをタップ済み（`tappedItems` に含む）
 * - `tappedItems` は `buildUserItemTapMap` で得た当該ユーザーの item 集合（未タップ = undefined）。
 * - `coupon` は当該ユーザーのクーポン横断アクティビティ（M-68 / バッジ表示と同じ和集合判定に
 *   するために `reactionActionsOf` へそのまま渡す。未指定は従来どおり eventTypeCounts のみ）。
 */
export function matchesCustomerFilter(
  u: Pick<AppUser, 'bookingCount' | 'eventTypeCounts' | 'lastInboundAt' | 'lastReplyAt'>,
  selected: ReadonlySet<string>,
  tappedItems: ReadonlySet<string> | undefined,
  coupon?: UserCouponActivity,
): boolean {
  if (selected.size === 0) return true;
  if (selected.has('booked') && isBookedCustomer(u)) return true;
  // M-147: 未返信（顧客の最終受信がこちらの最終返信より新しい）
  if (selected.has('awaiting_reply') && isAwaitingReply(u)) return true;
  let actions: ReactionActionKey[] | null = null;
  for (const key of selected) {
    if (key === 'booked' || key === 'awaiting_reply') continue;
    if (REACTION_ACTION_KEY_SET.has(key)) {
      actions ??= reactionActionsOf(u, coupon);
      if (actions.includes(key as ReactionActionKey)) return true;
      continue;
    }
    if (tappedItems?.has(key)) return true;
  }
  return false;
}

/** 流入区分の内訳（予約客 / 全体）。 */
export interface AudienceSummary {
  /** 総人数 */
  total: number;
  /** 予約経験のある顧客（bookingCount > 0） */
  booked: number;
}

/** 流入区分を集計する（pure）。 */
export function summarizeAudience(users: ReadonlyArray<AppUser>): AudienceSummary {
  const total = users.length;
  const booked = users.filter(isBookedCustomer).length;
  return { total, booked };
}

/** 行動アクションタグ 1 件分（保有人数）。 */
export interface ReactionActionCount {
  key: ReactionActionKey;
  count: number;
}

/**
 * 行動アクションタグ分布を集計する（pure / 全ユーザー対象 / M-59）。
 * `REACTION_ACTION_ORDER` の固定順で、各タグの保有人数を返す（非排他のため合計は
 * 総人数を超えうる）。0 人のタグも 0 として含め、バーの並びを安定させる。
 *
 * ユーザーごとに `reactionActionsOf` を 1 度だけ呼んでタリーする単一パス（O(N)）。
 * `activityByLineUserId` を渡すと coupon_usages 由来のアクティビティと和集合で判定する
 * （M-68 / 一覧のバッジ・フィルターと同じ基準に揃える。未指定は従来どおり）。
 */
export function summarizeReactionActions(
  users: ReadonlyArray<AppUser>,
  activityByLineUserId?: ReadonlyMap<string, UserCouponActivity>,
): ReactionActionCount[] {
  const counts = new Map<ReactionActionKey, number>(
    REACTION_ACTION_ORDER.map((key) => [key, 0]),
  );
  for (const u of users) {
    const coupon = u.lineUserId ? activityByLineUserId?.get(u.lineUserId) : undefined;
    for (const key of reactionActionsOf(u, coupon)) {
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  return REACTION_ACTION_ORDER.map((key) => ({ key, count: counts.get(key) ?? 0 }));
}

/** 興味タグ 1 件分（保有人数）。 */
export interface InterestTagCount {
  key: PromoInterestKey;
  count: number;
}

/**
 * 興味タグ分布を集計する（pure）。
 * 確度レベルは問わず「保有有無」で 1 人 1 カウント。`INTEREST_KEY_OPTIONS` の固定順で返し、
 * 保有 0 のキーも 0 として含める（バーの並びを安定させる）。
 */
export function summarizeInterestTags(users: ReadonlyArray<AppUser>): InterestTagCount[] {
  return INTEREST_KEY_OPTIONS.map(({ value }) => ({
    key: value,
    count: users.filter((u) => u.interestTags?.[value] != null).length,
  }));
}

/** 行動ログ種別 1 件分（延べ回数）。 */
export interface EventTypeCount {
  type: PromoEventType;
  count: number;
}

/**
 * 行動ログ種別ごとの延べ回数を集計する（pure）。
 * 全 user の `eventTypeCounts` を種別ごとに合算。`SEGMENT_EVENT_TYPE_OPTIONS` の種別順
 * （顧客行動のみ / `message_sent` は除外済み）で返し、合計 0 の種別は配列から落とす。
 */
export function summarizeEventTypes(users: ReadonlyArray<AppUser>): EventTypeCount[] {
  return SEGMENT_EVENT_TYPE_OPTIONS.map(({ value }) => ({
    type: value,
    count: users.reduce((sum, u) => sum + (u.eventTypeCounts?.[value] ?? 0), 0),
  })).filter((e) => e.count > 0);
}
